import { useEffect, useState } from "react";
import { fetchHistory, typicalLatencyMs } from "../lib/history";
import { ToolNavigation } from "./ToolNavigation";

/**
 * Top navigation strip: brand, in-app links, computed live-ish stats.
 *
 * Stats are honest: only "14 resolvers" (constant) + typical p50 latency
 * computed from /api/history's most recent day. We avoid the SaaS-mockup
 * habit of fake "99.7% up · 2.1M lookups/day · 0.4% errors" — those aren't
 * tracked, so we don't pretend.
 */
// Single source of truth, shared with scripts/_nav.mjs (which renders the same
// nav into the static pages). Edit src/nav-links.json to change both at once.
import navLinksJson from "../nav-links.json";

const NAV_LINKS = navLinksJson as [string, string][];

const normPath = (p: string) => (p !== "/" ? p.replace(/\/$/, "") : p);

export function TopNav() {
  const [typicalMs, setTypicalMs] = useState<number | null>(null);
  // Active link = the nav entry matching the current pathname. The home page
  // ("/") only highlights Lookup when nothing more specific matches.
  const current = typeof window !== "undefined" ? normPath(window.location.pathname) : "/";

  useEffect(() => {
    let cancelled = false;
    fetchHistory()
      .then((snap) => {
        if (cancelled) return;
        setTypicalMs(typicalLatencyMs(snap));
      })
      .catch(() => { /* leave as null; UI handles */ });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <header className={`topnav workspace-topnav${current !== "/" ? " workspace-tool-topnav" : ""}`} role="banner">
      <ToolNavigation current={current} />
      <a className="topnav-brand" href="/" aria-label="WhereIsDNS home">
        <svg className="brand-mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" />
          <ellipse cx="12" cy="12" rx="4" ry="9" />
          <path d="M3 12h18" />
        </svg>
        WhereIsDNS
      </a>
      <nav className="topnav-links" aria-label="Primary">
        {NAV_LINKS.filter(([href]) => ["/guide/dns-records", "/about"].includes(href)).map(([href, label]) => {
          const active = normPath(href) === current;
          return (
            <a key={href} href={href} className={active ? "active" : undefined} aria-current={active ? "page" : undefined}>
              {label}
            </a>
          );
        })}
      </nav>
      <div className="topnav-stats" aria-label="Service stats">
        <div className="stat"><b>14</b> resolvers</div>
        <div className="stat">
          <span className="stat-dot" aria-hidden="true" />
          streaming live
        </div>
        <div className="stat">
          <b>{typicalMs == null ? "…" : `${typicalMs}ms`}</b> typical p50
        </div>
      </div>
      {/* Mirrors the static markup in scripts/_nav.mjs. Click is handled by the
          delegated listener in index.html, and which icon shows is pure CSS,
          so this button carries no React state. */}
      <button
        type="button"
        className="theme-toggle"
        data-theme-toggle
        aria-label="Toggle light and dark theme"
        title="Toggle light and dark theme"
      >
        <svg className="icon-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
        <svg className="icon-moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      </button>
    </header>
  );
}
